import { Clock, Moon, Sun, Calendar, Trash2, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ShiftTemplate,
  formatTimeDisplay,
  DAYS_OF_WEEK
} from '@/types/shift-templates';

interface ShiftTemplateCardProps {
  template: ShiftTemplate;
  onEdit: (template: ShiftTemplate) => void;
  onDelete: (template: ShiftTemplate) => void;
}

export function ShiftTemplateCard({ template, onEdit, onDelete }: ShiftTemplateCardProps) {
  const startTime = formatTimeDisplay(template.default_start_time);
  const endTime = formatTimeDisplay(template.default_end_time);

  return (
    <Card
      className="relative cursor-pointer hover:border-primary/50 transition-colors group"
      onClick={() => onEdit(template)}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {template.is_overnight ? (
              <Moon className="h-5 w-5 text-indigo-500" />
            ) : (
              <Sun className="h-5 w-5 text-amber-500" />
            )}
            <CardTitle className="text-lg font-semibold">{template.name}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(template);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <ChevronRight className="h-5 w-5 text-muted-foreground ml-1" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Default hours */}
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {startTime} - {endTime}
          </span>
          {template.is_overnight && (
            <Badge variant="secondary" className="text-[12px]">
              Noturno
            </Badge>
          )}
        </div>

        {/* Variable hours indicator */}
        {template.has_variable_hours && template.day_specific_times && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Horários variáveis:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(template.day_specific_times).map(([dayKey, times]) => {
                const day = DAYS_OF_WEEK.find(d => d.value === Number(dayKey));
                if (!day || !times) return null;
                return (
                  <Badge key={dayKey} variant="outline" className="text-[12px]">
                    {day.short}: {times.start}-{times.end}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
